
import React, { useState } from 'react';
import { 
  Activity, 
  Heart, 
  Thermometer, 
  Wind, 
  Droplets,
  AlertCircle,
  Save,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useStore } from '../store';
import type { PhysicalAssessment, VitalSigns } from '../types/assessment';
import toast from 'react-hot-toast';

interface SystemSectionProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  isAbnormal: boolean;
  onAbnormalChange: (checked: boolean) => void;
  icon?: React.ReactNode;
}

const SystemSection: React.FC<SystemSectionProps> = ({
  title,
  value,
  onChange,
  isAbnormal,
  onAbnormalChange,
  icon
}) => {
  const [isExpanded, setIsExpanded] = useState(isAbnormal);

  return (
    <div className={`border rounded-lg ${isAbnormal ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
      <div
        className="p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {icon}
            <h4 className="font-medium">{title}</h4>
            {isAbnormal && (
              <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full">
                Abnormal
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <label 
              className="flex items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={isAbnormal}
                onChange={(e) => {
                  onAbnormalChange(e.target.checked);
                  if (e.target.checked) setIsExpanded(true);
                }}
                className="mr-2"
              />
              <span className="text-sm text-gray-600">Flag as abnormal</span>
            </label>
            {isExpanded ? <ChevronUp /> : <ChevronDown />}
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-4 pb-4">
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${title.toLowerCase()} findings...`}
            className="w-full px-3 py-2 border rounded-md resize-none"
            rows={3}
          />
        </div>
      )}
    </div>
  );
};

export default function PhysicalAssessmentPage() {
  const currentVisit = useStore((state) => state.currentVisit);
  const addAssessment = useStore((state) => state.addAssessment);
  const user = useStore((state) => state.user);

  const [vitals, setVitals] = useState<VitalSigns>({
    blood_pressure_systolic: undefined,
    blood_pressure_diastolic: undefined,
    pulse: undefined,
    temperature: undefined,
    respiratory_rate: undefined,
    oxygen_saturation: undefined,
    pain_level: 0
  });

  const [systems, setSystems] = useState({
    neurological: { findings: '', abnormal: false },
    cardiovascular: { findings: '', abnormal: false },
    respiratory: { findings: '', abnormal: false },
    gastrointestinal: { findings: '', abnormal: false },
    genitourinary: { findings: '', abnormal: false },
    musculoskeletal: { findings: '', abnormal: false },
    skin: { findings: '', abnormal: false }
  });

  const [additionalFindings, setAdditionalFindings] = useState('');
  const [interventions, setInterventions] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const hasAbnormalFindings = Object.values(systems).some(s => s.abnormal);

  const handleSave = async () => {
    if (!currentVisit || !user) {
      toast.error('No active visit selected');
      return;
    }

    setIsSaving(true);
    try {
      const assessment: PhysicalAssessment = {
        id: crypto.randomUUID(),
        visit_id: currentVisit.id,
        patient_id: currentVisit.patient_id,
        nurse_id: user.id,
        ...vitals,
        neurological: systems.neurological.findings,
        neurological_abnormal: systems.neurological.abnormal,
        cardiovascular: systems.cardiovascular.findings,
        cardiovascular_abnormal: systems.cardiovascular.abnormal,
        respiratory: systems.respiratory.findings,
        respiratory_abnormal: systems.respiratory.abnormal,
        gastrointestinal: systems.gastrointestinal.findings,
        gastrointestinal_abnormal: systems.gastrointestinal.abnormal,
        genitourinary: systems.genitourinary.findings,
        genitourinary_abnormal: systems.genitourinary.abnormal,
        musculoskeletal: systems.musculoskeletal.findings,
        musculoskeletal_abnormal: systems.musculoskeletal.abnormal,
        skin: systems.skin.findings,
        skin_abnormal: systems.skin.abnormal,
        additional_findings: additionalFindings,
        interventions,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      addAssessment(assessment);
      toast.success('Assessment saved successfully!');
      
      // Reset form
      setVitals({
        blood_pressure_systolic: undefined,
        blood_pressure_diastolic: undefined,
        pulse: undefined,
        temperature: undefined,
        respiratory_rate: undefined,
        oxygen_saturation: undefined,
        pain_level: 0
      });
      setSystems({
        neurological: { findings: '', abnormal: false },
        cardiovascular: { findings: '', abnormal: false },
        respiratory: { findings: '', abnormal: false },
        gastrointestinal: { findings: '', abnormal: false },
        genitourinary: { findings: '', abnormal: false },
        musculoskeletal: { findings: '', abnormal: false },
        skin: { findings: '', abnormal: false }
      });
      setAdditionalFindings('');
      setInterventions('');
    } catch (error) {
      toast.error('Failed to save assessment');
    } finally {
      setIsSaving(false);
    }
  };

  const isValidBP = vitals.blood_pressure_systolic && vitals.blood_pressure_diastolic &&
    vitals.blood_pressure_systolic > vitals.blood_pressure_diastolic;

  return (
    <div className="p-4 max-w-4xl mx-auto pb-24">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Physical Assessment</h2>
        {hasAbnormalFindings && (
          <div className="flex items-center text-red-600">
            <AlertCircle className="h-5 w-5 mr-1" />
            <span className="text-sm font-medium">Abnormal findings present</span>
          </div>
        )}
      </div>

      {/* Patient Info */}
      {currentVisit && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="text-sm text-blue-800">
            <p className="font-semibold">
              Patient: {currentVisit.patient?.first_name} {currentVisit.patient?.last_name}
            </p>
            <p>Visit Type: {currentVisit.visit_type}</p>
          </div>
        </div>
      )}

      {/* Vital Signs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center">
            <Activity className="h-5 w-5 mr-2 text-blue-600" />
            Vital Signs
          </h3>
        </div>
        
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Blood Pressure */}
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center">
              <Heart className="h-4 w-4 mr-1 text-red-500" />
              Blood Pressure (mmHg)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                placeholder="Systolic"
                value={vitals.blood_pressure_systolic || ''}
                onChange={(e) => setVitals({
                  ...vitals,
                  blood_pressure_systolic: e.target.value ? Number(e.target.value) : undefined
                })}
                className="w-24 px-3 py-2 border rounded-md"
              />
              <span>/</span>
              <input
                type="number"
                placeholder="Diastolic"
                value={vitals.blood_pressure_diastolic || ''}
                onChange={(e) => setVitals({
                  ...vitals,
                  blood_pressure_diastolic: e.target.value ? Number(e.target.value) : undefined
                })}
                className="w-24 px-3 py-2 border rounded-md"
              />
            </div>
            {vitals.blood_pressure_systolic && vitals.blood_pressure_diastolic && !isValidBP && (
              <p className="text-xs text-red-600 mt-1">Invalid BP values</p>
            )}
          </div>

          {/* Pulse */}
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center">
              <Activity className="h-4 w-4 mr-1 text-pink-500" />
              Pulse (BPM)
            </label>
            <input
              type="number"
              value={vitals.pulse || ''}
              onChange={(e) => setVitals({
                ...vitals,
                pulse: e.target.value ? Number(e.target.value) : undefined
              })}
              placeholder="60-100"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>

          {/* Temperature */}
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center">
              <Thermometer className="h-4 w-4 mr-1 text-orange-500" />
              Temperature (°F)
            </label>
            <input
              type="number"
              step="0.1"
              value={vitals.temperature || ''}
              onChange={(e) => setVitals({
                ...vitals,
                temperature: e.target.value ? Number(e.target.value) : undefined
              })}
              placeholder="98.6"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>

          {/* Respiratory Rate */}
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center">
              <Wind className="h-4 w-4 mr-1 text-blue-500" />
              Respiratory Rate
            </label>
            <input
              type="number"
              value={vitals.respiratory_rate || ''}
              onChange={(e) => setVitals({
                ...vitals,
                respiratory_rate: e.target.value ? Number(e.target.value) : undefined
              })}
              placeholder="12-20"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>

          {/* O2 Saturation */}
          <div>
            <label className="block text-sm font-medium mb-1 flex items-center">
              <Droplets className="h-4 w-4 mr-1 text-cyan-500" />
              O2 Saturation (%)
            </label>
            <input
              type="number"
              value={vitals.oxygen_saturation || ''}
              onChange={(e) => setVitals({
                ...vitals,
                oxygen_saturation: e.target.value ? Number(e.target.value) : undefined
              })}
              placeholder="95-100"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>

          {/* Pain Level */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Pain Level (0-10)
            </label>
            <select
              value={vitals.pain_level || 0}
              onChange={(e) => setVitals({
                ...vitals,
                pain_level: Number(e.target.value)
              })}
              className="w-full px-3 py-2 border rounded-md"
            >
              {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                <option key={n} value={n}>
                  {n} {n === 0 ? '(No pain)' : n === 10 ? '(Worst pain)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* System Assessments */}
      <div className="space-y-4 mb-6">
        <h3 className="font-semibold">System Assessment</h3>
        
        <SystemSection
          title="Neurological"
          value={systems.neurological.findings}
          onChange={(value) => setSystems({
            ...systems,
            neurological: { ...systems.neurological, findings: value }
          })}
          isAbnormal={systems.neurological.abnormal}
          onAbnormalChange={(checked) => setSystems({
            ...systems,
            neurological: { ...systems.neurological, abnormal: checked }
          })}
        />

        <SystemSection
          title="Cardiovascular"
          value={systems.cardiovascular.findings}
          onChange={(value) => setSystems({
            ...systems,
            cardiovascular: { ...systems.cardiovascular, findings: value }
          })}
          isAbnormal={systems.cardiovascular.abnormal}
          onAbnormalChange={(checked) => setSystems({
            ...systems,
            cardiovascular: { ...systems.cardiovascular, abnormal: checked }
          })}
        />

        <SystemSection
          title="Respiratory"
          value={systems.respiratory.findings}
          onChange={(value) => setSystems({
            ...systems,
            respiratory: { ...systems.respiratory, findings: value }
          })}
          isAbnormal={systems.respiratory.abnormal}
          onAbnormalChange={(checked) => setSystems({
            ...systems,
            respiratory: { ...systems.respiratory, abnormal: checked }
          })}
        />

        <SystemSection
          title="Gastrointestinal"
          value={systems.gastrointestinal.findings}
          onChange={(value) => setSystems({
            ...systems,
            gastrointestinal: { ...systems.gastrointestinal, findings: value }
          })}
          isAbnormal={systems.gastrointestinal.abnormal}
          onAbnormalChange={(checked) => setSystems({
            ...systems,
            gastrointestinal: { ...systems.gastrointestinal, abnormal: checked }
          })}
        />

        <SystemSection
          title="Genitourinary"
          value={systems.genitourinary.findings}
          onChange={(value) => setSystems({
            ...systems,
            genitourinary: { ...systems.genitourinary, findings: value }
          })}
          isAbnormal={systems.genitourinary.abnormal}
          onAbnormalChange={(checked) => setSystems({
            ...systems,
            genitourinary: { ...systems.genitourinary, abnormal: checked }
          })}
        />

        <SystemSection
          title="Musculoskeletal"
          value={systems.musculoskeletal.findings}
          onChange={(value) => setSystems({
            ...systems,
            musculoskeletal: { ...systems.musculoskeletal, findings: value }
          })}
          isAbnormal={systems.musculoskeletal.abnormal}
          onAbnormalChange={(checked) => setSystems({
            ...systems,
            musculoskeletal: { ...systems.musculoskeletal, abnormal: checked }
          })}
        />

        <SystemSection
          title="Skin"
          value={systems.skin.findings}
          onChange={(value) => setSystems({
            ...systems,
            skin: { ...systems.skin, findings: value }
          })}
          isAbnormal={systems.skin.abnormal}
          onAbnormalChange={(checked) => setSystems({
            ...systems,
            skin: { ...systems.skin, abnormal: checked }
          })}
        />
      </div>

      {/* Additional Findings */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4">
          <label className="block text-sm font-medium mb-2">
            Additional Findings
          </label>
          <textarea
            value={additionalFindings}
            onChange={(e) => setAdditionalFindings(e.target.value)}
            placeholder="Enter any additional findings or observations..."
            className="w-full px-3 py-2 border rounded-md resize-none"
            rows={3}
          />
        </div>
      </div>

      {/* Interventions */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4">
          <label className="block text-sm font-medium mb-2">
            Interventions Performed
          </label>
          <textarea
            value={interventions}
            onChange={(e) => setInterventions(e.target.value)}
            placeholder="Document any interventions performed during this assessment..."
            className="w-full px-3 py-2 border rounded-md resize-none"
            rows={3}
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="fixed bottom-20 md:bottom-4 left-0 right-0 p-4 bg-white border-t md:relative md:border-0 md:bg-transparent">
        <button
          onClick={handleSave}
          disabled={isSaving || !currentVisit}
          className={`w-full flex items-center justify-center py-3 px-4 rounded-md text-white font-medium transition-colors ${
            isSaving || !currentVisit
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          <Save className="h-5 w-5 mr-2" />
          {isSaving ? 'Saving...' : 'Save Assessment'}
        </button>
      </div>
    </div>
  );
}
